import * as React from 'react';
import {
  Badge,
  Button,
  Heading,
  HStack,
  Text,
  VStack,
  useColorModeValue,
  Box,
  Container
} from "@chakra-ui/react";
import { Card, CardBody } from '@chakra-ui/card';
import { useEffect, useState } from 'react';
import { Member } from '../models/member';
import { getUserFullName } from '../models/user';
import { useAuth } from '../components/auth';
import { useNavigate } from 'react-router-dom';
import { HTTP_STATUS_CODE_OK, MY_MEMBERSHIP_FEES_PAGE_ROUTE } from '../constants';
import { getMember } from '../services/member-service';
import { getMembershipFeesByMember } from '../services/product-service';
import { getNews } from '../services/news-service';
import { News } from '../models/news';
import MemberActivityDetails from "../components/member-activity-details";
import NewsSection from "../components/news-section";
import {isMobile} from "react-device-detect";

function Dashboard(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState(getUserFullName(auth.user));
  const [member, setMember] = useState<Member>();
  const [pendingMembershipFees, setPendingMembershipFees] = useState<number>();
  const [news, setNews] = useState<News[]>([]);

  const bgColor = useColorModeValue('#faf9f7', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const headingColor = useColorModeValue('green.700', 'green.300');

  useEffect(() => {
    const getMemberData = async (): Promise<void> => {
      const member = (await getMember(auth?.user?.selectedMember?.id)).response;
      if (member != null) {
        setMember(member);
      }
    }

    const getMemberMembershipFeesData = async (): Promise<void> => {
      const memberMembershipFeesResponse = await getMembershipFeesByMember(auth?.user?.selectedMember?.id);
      if (memberMembershipFeesResponse.statusCode === HTTP_STATUS_CODE_OK) {
        setPendingMembershipFees(memberMembershipFeesResponse.response?.pendingMembershipFees ?? 0)
      }
    }

    const getNewsData = async (): Promise<void> => {
      const newsResponse = await getNews();
      if (newsResponse.statusCode === HTTP_STATUS_CODE_OK) {
        setNews(newsResponse.response ?? []);
      }
    }

    getMemberData().catch(() => {});
    getMemberMembershipFeesData().catch(() => {});
    getNewsData().catch(() => {});
    setUsername(getUserFullName(auth.user));
  }, [auth?.user?.selectedMember?.id]);

  const handleMembershipFeesButton = (): void => {
    navigate(MY_MEMBERSHIP_FEES_PAGE_ROUTE, { replace: true });
  }

  const membershipFeesStatusCard = (): JSX.Element => {
    const hasPendingMembershipFees = (pendingMembershipFees ?? 0) > 0;
    return (
      <Card
        variant="filled"
        width={['100%', '100%', '50%']}
        alignSelf="start"
        bg={cardBg}
        borderColor={hasPendingMembershipFees ? 'red.200' : 'green.200'}
        borderWidth="2px"
        _hover={{
          transform: 'translateY(-4px)',
          boxShadow: 'xl',
          borderColor: hasPendingMembershipFees ? 'red.300' : 'green.300'
        }}
        transition="all 0.3s ease-in-out"
      >
        <CardBody p={6}>
          <VStack align="start" spacing={4}>
            <Box>
              <Heading size="lg" color={headingColor} mb={2}>
                🗓️ Cuotas sociales
              </Heading>
              <HStack spacing={3}>
                <Text fontSize="md" color={textColor} fontWeight="medium">
                  Situación actual:
                </Text>
                {hasPendingMembershipFees ? (
                  <Badge
                    colorScheme="red"
                    variant="solid"
                    fontSize="sm"
                    px={3}
                    py={1}
                    borderRadius="full"
                  >
                    ⚠️ Deuda
                  </Badge>
                ) : (
                  <Badge
                    colorScheme="green"
                    variant="solid"
                    fontSize="sm"
                    px={3}
                    py={1}
                    borderRadius="full"
                  >
                    ✅ Al día
                  </Badge>
                )}
              </HStack>
            </Box>

            <Box>
              {hasPendingMembershipFees ? (
                <VStack align="start" spacing={2}>
                  <Text fontSize="md" color={textColor} lineHeight="1.6">
                    Tenés{' '}
                    <Text as="span" fontWeight="bold" color="red.500">
                      {pendingMembershipFees}
                    </Text>{' '}
                    cuotas sociales pendientes de pago.
                  </Text>
                  <Text fontSize="md" color={textColor} lineHeight="1.6">
                    Pero no te preocupes: podés regularizar tu situación fácil y rápidamente.
                  </Text>
                </VStack>
              ) : (
                <Text fontSize="md" color={textColor} lineHeight="1.6">
                  🎉 ¡Felicitaciones! ¡Estás al día con tus cuotas sociales!
                </Text>
              )}
            </Box>

            <Button
              colorScheme="green"
              size="lg"
              onClick={handleMembershipFeesButton}
              width="full"
              leftIcon={<span>💳</span>}
            >
              Ver mis cuotas sociales
            </Button>

            <Box pt={2}>
              <Text fontSize="sm" color="gray.500" fontStyle="italic" lineHeight="1.5">
                💡 Recordá que el pago de las cuotas sociales constituye uno de los
                principales ingresos para mantener a nuestro club en pie.
              </Text>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  const getMemberActivities = () => {
    return member?.memberActivities.map((activity) => {
      if (!activity.activity.isInsurance) {
        return <MemberActivityDetails key={activity.id} activity={activity.activity}/>
      }
      return null;
    });
  }

  const memberActivities = (): JSX.Element => {
    return (
      <Card
        variant="filled"
        width={['100%', '100%', '50%']}
        alignSelf="start"
        bg={cardBg}
        borderColor="green.200"
        borderWidth="2px"
        _hover={{
          transform: 'translateY(-4px)',
          boxShadow: 'xl',
          borderColor: 'green.300'
        }}
        transition="all 0.3s ease-in-out"
      >
        <CardBody p={6}>
          <VStack align="start" spacing={4}>
            <Box>
              <Heading size="lg" color={headingColor} mb={2}>
                🏃‍♂️ Actividades
              </Heading>
              <Text fontSize="md" color={textColor} lineHeight="1.6">
                Estás dado de alta en las siguientes actividades:
              </Text>
            </Box>

            <VStack align="start" spacing={3} width="full">
              {getMemberActivities()}
            </VStack>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  const getMobileDisposition = () => {
    return (
      <VStack width="100%" spacing={8}>
        <NewsSection news={news} />
        {membershipFeesStatusCard()}
        {memberActivities()}
      </VStack>
    )
  };

  const getDesktopDisposition = () => {
    return (
      <VStack width="100%" spacing={8}>
        <NewsSection news={news} />
        <HStack width="100%" spacing={8} align="start">
          {membershipFeesStatusCard()}
          {memberActivities()}
        </HStack>
      </VStack>
    )
  };

  return (
    <Box minH="100vh" bg={bgColor}>
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="start">
          {/* Saludo de bienvenida */}
          <Box
            bg={cardBg}
            p={6}
            borderRadius="xl"
            boxShadow="lg"
            width="full"
            borderColor="green.200"
            borderWidth="2px"
          >
            <Heading size="lg" color={headingColor} mb={2}>
              🌟 ¡Hola de nuevo, <Text as="span" color="green.600">{username}</Text>!
            </Heading>
            <Text fontSize="md" color={textColor}>
              Bienvenido/a al portal de socios del <b>Club Atlético Pacífico</b>.
            </Text>
          </Box>

          {isMobile ? getMobileDisposition() : getDesktopDisposition()}
        </VStack>
      </Container>
    </Box>
  );
}

export default Dashboard;
